import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import {
    observeElementRect,
    type Rect,
    type Virtualizer,
} from "@tanstack/react-virtual";
import { useEditContext, useListEditContext } from "../editing/useEditContext";
import { LoadMoreRows } from "../features/loadMore/LoadMoreRows";
import { useLoadMore } from "../features/loadMore/useLoadMore";
import { countSeparatorRows } from "../features/rowKinds";
import { DisabledReasonContext } from "../features/selection/disabledReason";
import type { GridController } from "../focus/gridController";
import { selectFocusedRowId } from "../focus/navigation";
import { useKeepFocusInRows } from "../focus/useKeepFocusInRows";
import { ROW_HEIGHT_BY_DENSITY } from "../layout/density";
import { useRowOrderMotion } from "../motion/useRowOrderMotion";
import { getPageRowOffset } from "../tableOptions";
import type { DataGridInstance } from "../types";
import { isRowInView } from "./isRowInView";
import { createMemoRow } from "./Row";
import { SeparatorRow } from "./SeparatorRow";
import { useConstant } from "@hooks/useConstant";
import { useRowLayout } from "./rowLayout";
import { useKeptRowsRangeExtractor } from "./useKeptRowsRangeExtractor";
import { useRowVirtualizer } from "./useRowVirtualizer";
import styles from "./Grid.module.scss";

const OVERSCAN = 8;

const toViewportRect = (rect: Rect, fallback: Rect | undefined): Rect => {
    if (fallback && rect.width === 0 && rect.height === 0) {
        return fallback;
    }
    return {
        width: rect.width,
        height: Math.min(rect.height, window.innerHeight),
    };
};

/**
 * Viewport measurement with two guards. A viewport taller than the window
 * means its container has no height (or styles have not loaded yet); without
 * a cap the virtualizer would mount every row. With `initialRect` set, a
 * zero-size measurement (jsdom, a hidden parent) keeps the initial rect.
 */
const observeViewportRect =
    (fallback: Rect | undefined) =>
    (
        instance: Virtualizer<HTMLDivElement, Element>,
        onRect: (rect: Rect) => void,
    ) =>
        observeElementRect(instance, (rect) =>
            onRect(toViewportRect(rect, fallback)),
        );

/**
 * The rows to draw. TanStack hands out a new array whenever they change, so
 * reading them through a hook tells the compiler they are read-only, and the
 * row method calls below don't count as edits to them.
 */
const useDisplayRows = <TData extends RowData>(
    table: DataGridInstance<TData>["table"],
) => table.getRowModel().rows;

interface BodyProps<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
    viewportElement: HTMLDivElement | null;
    headerHeight: number;
    headerRowCount: number;
    /** Height of the sticky totals row, kept clear when scrolling to a row. */
    footerHeight: number;
}

/**
 * Virtualized body rowgroup over `table.getRowModel().rows`. Keeps the
 * focused row mounted while it is scrolled away, keeps DOM focus in the rows
 * when they change under it, and drives load more on scroll.
 */
export const Body = <TData extends RowData>({
    instance,
    controller,
    viewportElement,
    headerHeight,
    headerRowCount,
    footerHeight,
}: Readonly<BodyProps<TData>>) => {
    const { table, density, atoms, hasCellNavigation, isTree, rowKinds } =
        instance;
    const rows = useDisplayRows(table);
    const rowHeight = ROW_HEIGHT_BY_DENSITY[density];
    const separatorCounts = countSeparatorRows({
        rows,
        isSeparator: rowKinds.isSeparator,
        getCount: rowKinds.getSeparatorCount,
    });
    const rowIndexById = useMemo(
        () => new Map(rows.map((row, index) => [row.id, index])),
        [rows],
    );
    const { cellSelection, expanded } = table.atoms;
    const getFocusedIndex = () => {
        const rowId = selectFocusedRowId(cellSelection.get());
        return rowId === null ? -1 : (rowIndexById.get(rowId) ?? -1);
    };
    const dragRowId = useSelector(controller.dragSource, (source) =>
        source?.kind === "row" ? source.id : null,
    );
    const dragIndex =
        dragRowId === null ? -1 : (rowIndexById.get(dragRowId) ?? -1);
    controller.rowMotion.noteData({
        data: table.getCoreRowModel(),
        // A separator surviving a reload would read as a kept row.
        getRowIds: () =>
            table
                .getCoreRowModel()
                .flatRows.filter((row) => !rowKinds.isSeparator(row.original))
                .map(({ id }) => id),
    });

    const rangeExtractor = useKeptRowsRangeExtractor({
        getFocusedIndex,
        dragIndex,
    });
    const layout = useRowLayout({
        table,
        columnVariants: atoms.columnVariants,
    });

    const estimateSize = useCallback(() => rowHeight, [rowHeight]);
    const getItemKey = useCallback(
        (index: number) => rows[index]?.id ?? index,
        [rows],
    );

    const observeRect = useConstant(() =>
        observeViewportRect(instance.virtualization?.initialRect),
    );
    const { virtualizer, virtualItems, totalSize, rangeEndIndex } =
        useRowVirtualizer({
            count: rows.length,
            getScrollElement: () => viewportElement,
            estimateSize,
            getItemKey,
            overscan: OVERSCAN,
            rangeExtractor,
            scrollMargin: headerHeight,
            scrollPaddingStart: headerHeight,
            scrollPaddingEnd: footerHeight,
            initialRect: instance.virtualization?.initialRect,
            observeElementRect: observeRect,
        });

    useLayoutEffect(() => {
        virtualizer.measure();
    }, [virtualizer, rowHeight, layout.hasWrap]);

    useEffect(() => {
        const subscription = expanded.subscribe(controller.markExpanded);
        return () => subscription.unsubscribe();
    }, [expanded, controller]);

    useLoadMore({
        instance,
        controller,
        rowCount: rows.length,
        rangeEndIndex,
        getFocusedIndex,
    });

    const { optionsRef } = instance;
    // Table methods close over the core table (row and cell methods use `this`),
    // so `getRow` keeps one identity while `table` changes with root state.
    const { getRow } = table;
    const getDisabledReason = (rowId: string) => {
        const reason = optionsRef.current.rowSelection?.getDisabledReason;
        const row = reason ? getRow(rowId, true) : undefined;
        return row ? reason?.(row.original) : undefined;
    };

    useLayoutEffect(() => {
        controller.setRowAccess({
            rowIndexById,
            scrollToRowIndex: (index, align = "auto") => {
                if (align === "auto" && isRowInView(virtualizer, index)) {
                    return;
                }
                virtualizer.scrollToIndex(index, { align });
            },
            getRowIndexAtOffset: (offset) =>
                virtualizer.getVirtualItemForOffset(offset + headerHeight)
                    ?.index ?? -1,
            getRowBounds: (index) => {
                const item = virtualizer.measurementsCache[index];
                return (
                    item && {
                        start: item.start - headerHeight,
                        size: item.size,
                    }
                );
            },
        });
        return () => controller.setRowAccess(null);
    }, [controller, headerHeight, rowIndexById, virtualizer]);

    useKeepFocusInRows({ instance, controller, getFocusedIndex });

    const measureRef = layout.hasWrap ? virtualizer.measureElement : undefined;
    const MemoRow = useConstant(createMemoRow<TData>);
    const editContext = useEditContext({ instance, controller });
    const listEditContext = useListEditContext({ instance, controller });
    const pageOffset = getPageRowOffset(table);
    const hasRowSelection = Boolean(table.options.enableRowSelection);
    const positions = new Map(
        virtualItems.map((item) => [
            rows[item.index]?.id ?? "",
            item.start - headerHeight,
        ]),
    );
    useRowOrderMotion({
        controller,
        isEnabled: instance.motion.animateRowOrder,
        sorting: table.state.sorting,
        positions,
    });

    return (
        <DisabledReasonContext.Provider value={getDisabledReason}>
            <div
                ref={(element) => controller.setBody(element)}
                role="rowgroup"
                className={styles.body}
                style={{ height: totalSize }}
            >
                {virtualItems.map((item) => {
                    const row = rows[item.index];
                    if (!row) {
                        return null;
                    }
                    const start = item.start - headerHeight;
                    const ariaRowIndex =
                        headerRowCount + pageOffset + item.index + 1;
                    if (rowKinds.isSeparator(row.original)) {
                        return (
                            <SeparatorRow
                                key={row.id}
                                rowId={row.id}
                                start={start}
                                ariaRowIndex={ariaRowIndex}
                                virtualIndex={item.index}
                                measureRef={measureRef}
                                columnCount={layout.colIndexById.size}
                                attributes={rowKinds.getAttributes(
                                    row.original,
                                )}
                                count={separatorCounts.get(row.id) ?? 0}
                            >
                                {rowKinds.renderSeparator(row.original)}
                            </SeparatorRow>
                        );
                    }
                    return (
                        <MemoRow
                            key={row.id}
                            row={row}
                            virtualIndex={item.index}
                            measureRef={measureRef}
                            start={start}
                            ariaRowIndex={ariaRowIndex}
                            isExpanded={row.getIsExpanded()}
                            isGroupEnd={
                                instance.gridLines === "groups" &&
                                (rows[item.index + 1]?.depth ?? 0) === 0
                            }
                            layout={layout}
                            controller={controller}
                            atoms={atoms}
                            hasCellNavigation={hasCellNavigation}
                            hasListEditing={instance.hasListEditing}
                            hasFillHandle={instance.hasFillHandle}
                            hasRowSelection={hasRowSelection}
                            isTree={isTree}
                            hasParentSummaries={instance.hasParentSummaries}
                            editContext={editContext}
                            listEditContext={listEditContext}
                            rowKinds={rowKinds}
                            tableMeta={table.options.meta}
                            listEditing={instance.listEditing}
                        />
                    );
                })}
            </div>
            <LoadMoreRows
                instance={instance}
                ariaRowIndex={headerRowCount + pageOffset + rows.length + 1}
            />
        </DisabledReasonContext.Provider>
    );
};
